import { createReducer, PayloadAction, isAnyOf, isRejected } from '@reduxjs/toolkit';

import { ERRORProtect, ErrorType } from '@src/types/errors';
import { testKey, handleLocalStorage } from '@utils';

import { errorBuilder, loadersBuilder } from '@modules/builders';
import { ProtectNextEnum, VerifyNextEnum } from '@modules/types';

import loginActions from './actions';
import initialState, { LoginStateType } from './state';
import type * as LoginTypes from './types';
import accountsActions from '../accounts/actions';
import userActions from '../user/actions';
import withdrawActions from '../withdraw/actions';

const { setItem } = handleLocalStorage;
const { verifyOTP } = ProtectNextEnum;

const verificationStep = (
  state: LoginStateType,
  action: PayloadAction<{
    data: {
      token?: string;
      timeout?: number;
      key?: string;
      code_length?: number;
      edit_token?: string;
      masked?: string;
      email?: string;
      phone?: string;
      next?: ProtectNextEnum | VerifyNextEnum;
    };
    status?: number;
  }>
) => {
  const { data, status } = action.payload;
  if (data.next === verifyOTP && data.token) {
    state.verification.token = data.token;
  } else if (data.token) {
    setItem('isStrongModal', true);
    if (data.timeout) state.idleTimeout = data.timeout;
    state.token = data.token;
    state.isLoggedIn = true;
    state.verification = {};
    delete state.status;
    setItem('idle_timestamp', new Date().getTime());
  } else {
    if (data.key && data.code_length) testKey(action.payload.data.key);
    state.verification.timeout = data.timeout;
    state.verification.key = data.key;
    state.verification.code_length = data.code_length;
    state.verification.editToken = data.edit_token;
    state.status = status;
    if (data.masked) {
      const type = data.code_length === 4 ? 'phone' : 'email';
      state.verification[type] = data.masked;
    }
    setItem('verification', state.verification);
  }
};

export const loginReducer = createReducer(initialState, builder => {
  builder.addCase(loginActions.logout.fulfilled, state => {
    state.isLoggedIn = false;
    delete state.token;
  });

  builder.addCase(loginActions.clearProtectNext, state => {
    if (state.protectNext) delete state.protectNext;
  });

  builder.addCase(loginActions.clearErrors, state => {
    state.errors = {};
  });

  builder.addCase(loginActions.updateTimeout, (state, action) => {
    state.verification.timeout = action.payload;
  });

  builder.addCase(loginActions.resetPasswordVerifyOTP.fulfilled, (state, action) => {
    state.verification.key = action.payload.data.key;
  });

  builder.addCase(userActions.changeEmailVerifyOTP.fulfilled, (state, action) => {
    state.verification.token = action.payload.data.token;
  });

  builder.addCase(loginActions.resetPasswordVerifyPhone.fulfilled, (state, action) => {
    if (action.payload.data.next === verifyOTP && action.payload.data.token) {
      state.verification.token = action.payload.data.token;
      state.verification.code_length = 6;
    }
  });

  builder.addCase(loginActions.login.pending, (state, action) => {
    const { phone, email } = action.meta?.arg;

    if (email) state.verification.email = email;
    if (phone) state.verification.phone = phone;
  });

  builder.addCase(userActions.changeEmailSendNewEmail.pending, (state, action) => {
    state.verification.email = action.meta.arg.email;
  });

  builder.addCase(loginActions.setEmail.pending, (state, action) => {
    state.verification.email = action.meta.arg.email;
  });

  builder.addCase(loginActions.keepAlive.rejected, (state, action) => {
    const payload = (action as PayloadAction<ERRORProtect>)?.payload;
    if (payload?.code === CONST.errorCodes.keepAliveOTP) state.isLoggedIn = true;
    if (payload?.data?.next) state.protectNext = payload?.data?.next;
  });

  builder.addCase(loginActions.keepAlive.fulfilled, state => {
    if (!state.isLoggedIn) state.isLoggedIn = true;
  });

  builder.addCase(withdrawActions.withdrawVerifyEmail.fulfilled, (state, action) => {
    if (!!action.payload.data.next) state.verification.code_length = 6;
  });

  builder.addMatcher(isRejected(), (state, action) => {
    const code = (action as PayloadAction<ErrorType>)?.payload?.code;

    if (state.isLoggedIn && +code === CONST.errorCodes.unauthorized) {
      state.isLoggedIn = false;
    }
  });

  builder.addMatcher(
    isAnyOf(
      loginActions.login.fulfilled,
      loginActions.verifyPhone.fulfilled,
      loginActions.verifyPassword.fulfilled,
      loginActions.setEmail.fulfilled,
      loginActions.verifyEmail.fulfilled,
      loginActions.resetPasswordEmail.fulfilled,
      loginActions.resetPasswordPhone.fulfilled,
      loginActions.resetPasswordVerifyEmail.fulfilled,
      loginActions.resetPasswordVerifyPhone.fulfilled,
      loginActions.setPassword.fulfilled,
      loginActions.resendVerification.fulfilled,
      userActions.changeEmailSendOldEmail.fulfilled,
      userActions.changeEmailSendNewEmail.fulfilled,
      userActions.changeEmailVerifyOldEmail.fulfilled,
      userActions.changeEmailVerifyPhone.fulfilled,
      userActions.changeEmailVerifyEmail.fulfilled,
      userActions.changePhoneVerifyOTP.fulfilled,
      userActions.changePhone.fulfilled,
      userActions.changePhoneVerifyPhone.fulfilled,
      userActions.changePhoneVerifyEmail.fulfilled,
      userActions.changePasswordSendEmail.fulfilled,
      userActions.changePasswordSetPassword.fulfilled,
      userActions.changeNewPassword.fulfilled,
      userActions.editUser.fulfilled,
      userActions.toggleStrongSecurity.fulfilled,
      withdrawActions.withdrawResendVerification.fulfilled,
      withdrawActions.withdraw.fulfilled,
      accountsActions.accountWithdraw.fulfilled,
      accountsActions.accountWithdrawVerifyResend.fulfilled
    ),
    verificationStep
  );

  errorBuilder('login', builder, [loginActions.resetPasswordPhone, loginActions.keepAlive]);
  loadersBuilder('login', builder);
});

export { loginActions, LoginTypes };
